/**
 * Client-side parser for the `session_control_propose` marker.
 *
 * The marker is produced by the daemon tool and appended to the assistant
 * message. It is only a UI proposal: tapping the card still goes through the
 * existing signed `session.command` path, which revalidates target/capability.
 * Malformed or unknown markers are ignored (never rendered as executable UI).
 */

import type { SessionSource } from '@luminis/protocol';

export type SessionControlProposalCommand = 'message' | 'interrupt';

export interface SessionControlProposal {
  id: string;
  source: SessionSource;
  session_id: string;
  command: SessionControlProposalCommand;
  text?: string;
  expires_at: number;
}

const MARKER = /<!--luminis-control:([A-Za-z0-9_-]+)-->/gu;
const SOURCES: ReadonlySet<string> = new Set(['dsh', 'codex', 'opencode', 'vscode', 'luminis']);
const MAX_TEXT_CHARS = 8 * 1024;

function decodeBase64Url(token: string): unknown {
  const normalized = token.replace(/-/gu, '+').replace(/_/gu, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const json = atob(padded);
  return JSON.parse(json) as unknown;
}

function parseProposal(raw: unknown): SessionControlProposal | undefined {
  if (raw === null || typeof raw !== 'object') return undefined;
  const value = raw as Record<string, unknown>;
  if (value['v'] !== 1) return undefined;
  if (typeof value['id'] !== 'string' || !value['id'].startsWith('prop_')) return undefined;
  if (typeof value['source'] !== 'string' || !SOURCES.has(value['source'])) return undefined;
  if (typeof value['session_id'] !== 'string') return undefined;
  const sessionId = value['session_id'];
  if (sessionId.length === 0 || sessionId.length > 512) return undefined;
  const command = value['command'];
  if (command !== 'message' && command !== 'interrupt') return undefined;
  if (typeof value['expires_at'] !== 'number' || !Number.isFinite(value['expires_at'])) {
    return undefined;
  }
  if (value['text'] !== undefined && typeof value['text'] !== 'string') return undefined;
  const text = typeof value['text'] === 'string' ? value['text'] : undefined;
  if (command === 'message' && (text === undefined || text.trim().length === 0)) return undefined;
  if (command === 'interrupt' && text !== undefined && text.length > 0) return undefined;
  if (text !== undefined && text.length > MAX_TEXT_CHARS) return undefined;
  return {
    id: value['id'],
    source: value['source'] as SessionSource,
    session_id: sessionId,
    command,
    ...(text === undefined ? {} : { text }),
    expires_at: value['expires_at'],
  };
}

/** Strip valid markers from rendered text and return deduplicated proposals. */
export function extractSessionControlProposals(content: string): {
  text: string;
  proposals: SessionControlProposal[];
} {
  const proposals: SessionControlProposal[] = [];
  const seen = new Set<string>();
  for (const match of content.matchAll(MARKER)) {
    const token = match[1];
    if (token === undefined) continue;
    try {
      const parsed = parseProposal(decodeBase64Url(token));
      if (parsed !== undefined && !seen.has(parsed.id)) {
        seen.add(parsed.id);
        proposals.push(parsed);
      }
    } catch {
      // Invalid marker: leave it out of the UI entirely.
    }
  }
  const text = content
    .replace(MARKER, '')
    .replace(/\n{3,}/gu, '\n\n')
    .trim();
  return { text, proposals };
}
